import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText } from "lucide-react";
import { MinimalShell } from "../components/layout/MinimalShell";
import { LeadForm } from "../components/shared/LeadCapture";
import { RESONANCE_TOUCHSTONES } from "../data/siteContent";

export default function ResonanceTouchstones() {
  const reduce = useReducedMotion();
  const [downloadReady, setDownloadReady] = useState(false);
  const previews = RESONANCE_TOUCHSTONES.previewIndexes.map((index) => RESONANCE_TOUCHSTONES.statements[index]);

  return (
    <MinimalShell>
      <section data-testid="resonance-touchstones-page" className="relative min-h-screen overflow-hidden pt-32 md:pt-40 pb-20 grain">
        <div className="aurora-weave" />
        <div className="line-weave" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-20 items-center">
          <motion.div initial={{ opacity: 0, y: reduce ? 0 : 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduce ? 0 : 0.65, ease: [0.22, 1, 0.36, 1] }}>
            <div className="inline-flex items-center gap-3 text-[#E7CC86] mb-7">
              <FileText size={21} aria-hidden="true" />
              <span className="text-sm tracking-[0.2em] uppercase">A Resonance Intelligence resource</span>
            </div>
            <h1 className="font-display font-bold text-5xl sm:text-6xl leading-[1.02] text-cream">{RESONANCE_TOUCHSTONES.title}</h1>
            <p className="mt-6 font-serif italic text-3xl md:text-4xl leading-tight text-[#E7CC86]">{RESONANCE_TOUCHSTONES.subtitle}</p>
            <div className="mt-10 space-y-5 max-w-xl">
              {previews.map((statement, index) => (
                <motion.p key={statement} initial={{ opacity: 0, x: reduce ? 0 : -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: reduce ? 0 : 0.5, delay: reduce ? 0 : 0.18 + index * 0.1, ease: [0.22, 1, 0.36, 1] }} className="font-serif italic text-xl md:text-2xl leading-snug text-cream/85">
                  “{statement}”
                </motion.p>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: reduce ? 0 : 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduce ? 0 : 0.65, delay: reduce ? 0 : 0.16, ease: [0.22, 1, 0.36, 1] }} className="glass rounded-2xl p-7 md:p-10">
            <FileText size={28} className="text-[#E7CC86] mb-6" aria-hidden="true" />
            <h2 className="font-display font-semibold text-3xl leading-tight text-cream mb-3">Receive the PDF</h2>
            <p className="text-cream/70 leading-relaxed mb-7">A collection of 31 touchstones for remembering what matters and living by it.</p>
            {downloadReady ? (
              <div data-testid="touchstones-download-ready" className="text-center py-3">
                <p className="font-display text-2xl text-cream mb-3">Your Touchstones are ready.</p>
                <a href="/assets/resonance-touchstones.pdf" download="resonance-touchstones.pdf" data-testid="touchstones-download" className="btn-gold px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2">
                  Download the PDF <FileText size={18} aria-hidden="true" />
                </a>
              </div>
            ) : (
              <LeadForm
                source={RESONANCE_TOUCHSTONES.source}
                avatar={RESONANCE_TOUCHSTONES.avatar}
                interest={RESONANCE_TOUCHSTONES.interest}
                cta="Receive the Touchstones"
                onSuccess={() => setDownloadReady(true)}
              />
            )}
          </motion.div>
        </div>
      </section>
    </MinimalShell>
  );
}
